/**
 * Pocket money by country (researched 2026-10-14): which currency a family most likely uses, and what is
 * typical per month by age, shown as a hint while parents set up pocket money. Weekly figures are
 * converted to months (× 4.33) and rounded. Only countries with a published survey or official
 * guideline; none is shown for the rest (e.g. Iceland, where no reliable figures were found).
 *
 * Sources:
 *  DK  Danske Bank survey (via skagenonline.dk): under 100 / 50–200 / 200–500 kr a month
 *  SE  Swedbank & Sparbankerna: ~20 kr/week at 6–7, ~200 kr/month at 11, 300–500 kr at 13–15
 *  NO  DNB: 64 kr/week at 6–13, 264 kr/week at 14–17
 *  DE  Jugendamt / BMFSFJ Taschengeldtabelle 2026
 *  FR  Pixpay/Discurv 2025–2026: ~23 € at 8–10, ~20 € at 10–12, ~35 € at 16–18
 *  FI  Nordea family survey: ~5 €/week in primary school, 9–15 €/week in lower secondary
 *  ES  Instituto Santalucía: 2–4 €/week at 7–9, 5–8 €/week at 10–12
 *  PL  BNP Paribas: ~50 zł at 6–10, ~80 zł at 11–13, ~100 zł at 14–18
 *  GB  NatWest Rooster Money Pocket Money Index 2025: regular pocket money £3.85/week (all ages)
 *  US  Wells Fargo 2025 (medians): $15/week at 5–8, $20 at 9–14, $25 at 15–17
 */
export type TypicalBand = { ages: string; min?: number; max?: number; about?: number; under?: number };
export type CountryTypical = { currency: string; source: string; bands: TypicalBand[] };

export const TYPICAL: Record<string, CountryTypical> = {
  DK: { currency: 'DKK', source: 'Danske Bank', bands: [{ ages: '6–10', under: 100 }, { ages: '11–14', min: 50, max: 200 }, { ages: '15–17', min: 200, max: 500 }] },
  SE: { currency: 'SEK', source: 'Swedbank', bands: [{ ages: '6–7', about: 85 }, { ages: '11', about: 200 }, { ages: '13–15', min: 300, max: 500 }] },
  NO: { currency: 'NOK', source: 'DNB', bands: [{ ages: '6–13', about: 280 }, { ages: '14–17', about: 1150 }] },
  DE: { currency: 'EUR', source: 'Jugendamt (Taschengeldtabelle 2026)', bands: [{ ages: '6–7', min: 9, max: 13 }, { ages: '8–9', min: 13, max: 17 }, { ages: '10–11', min: 15, max: 25 }, { ages: '12–13', min: 20, max: 30 }, { ages: '14–15', min: 25, max: 45 }, { ages: '16–17', min: 40, max: 60 }] },
  FR: { currency: 'EUR', source: 'Pixpay 2025', bands: [{ ages: '8–10', about: 23 }, { ages: '10–12', about: 20 }, { ages: '16–18', about: 35 }] },
  FI: { currency: 'EUR', source: 'Nordea', bands: [{ ages: '7–12', about: 22 }, { ages: '13–15', min: 40, max: 65 }] },
  ES: { currency: 'EUR', source: 'Instituto Santalucía', bands: [{ ages: '7–9', min: 9, max: 17 }, { ages: '10–12', min: 22, max: 35 }] },
  PL: { currency: 'PLN', source: 'BNP Paribas', bands: [{ ages: '6–10', about: 50 }, { ages: '11–13', about: 80 }, { ages: '14–18', about: 100 }] },
  GB: { currency: 'GBP', source: 'NatWest Rooster Money 2025', bands: [{ ages: '5–17', about: 17 }] },
  US: { currency: 'USD', source: 'Wells Fargo 2025', bands: [{ ages: '5–8', about: 65 }, { ages: '9–14', about: 87 }, { ages: '15–17', about: 108 }] },
};

const REGION_CURRENCY: Record<string, string> = {
  DK: 'DKK', FO: 'DKK', GL: 'DKK', SE: 'SEK', NO: 'NOK', IS: 'ISK', PL: 'PLN', GB: 'GBP', US: 'USD',
  AU: 'AUD', CA: 'CAD', NZ: 'NZD', CH: 'CHF',
  DE: 'EUR', AT: 'EUR', FR: 'EUR', BE: 'EUR', FI: 'EUR', ES: 'EUR', IE: 'EUR', NL: 'EUR', LU: 'EUR',
};
const LANGUAGE_CURRENCY: Record<string, string> = {
  da: 'DKK', fo: 'DKK', sv: 'SEK', nb: 'NOK', is: 'ISK', pl: 'PLN', de: 'EUR', fr: 'EUR', fi: 'EUR', es: 'EUR', en: 'GBP',
};

/** The browser's region ("DK" from "da-DK"), if it has one. */
function browserRegion(): string | null {
  try {
    const tag = (navigator.languages && navigator.languages[0]) || navigator.language || '';
    const m = /[-_]([A-Za-z]{2})\b/.exec(tag);
    return m ? m[1].toUpperCase() : null;
  } catch {
    return null;
  }
}

/** A sensible first currency: the browser's country if known, else the app language's. */
export function defaultCurrency(language: string, region: string | null = browserRegion()): string {
  if (region && REGION_CURRENCY[region]) return REGION_CURRENCY[region];
  return LANGUAGE_CURRENCY[language] ?? 'EUR';
}

/** Which country's figures to show for this currency (euro: by the app's language). */
export function typicalCountry(currency: string, language: string): string | null {
  const byCurrency: Record<string, string> = { DKK: 'DK', SEK: 'SE', NOK: 'NO', PLN: 'PL', GBP: 'GB', USD: 'US' };
  if (byCurrency[currency]) return byCurrency[currency];
  if (currency === 'EUR') return ({ de: 'DE', fr: 'FR', fi: 'FI', es: 'ES' } as Record<string, string>)[language] ?? null;
  return null;
}
